/* eslint-disable */
import React, { useState, useEffect } from "react";
import { FaArrowRight, FaCheck } from "react-icons/fa";
import { Link } from "react-router-dom";
import FundingLabApplyModal from "./FundingLabApplyModal";

// HashRouter owns the URL hash, so a plain href="#id" navigates the router
// instead of scrolling (it blanks the page — no route matches "#id"). Scroll
// manually and never let the hash change reach the router.
const scrollToId = (id) => (e) => {
  e.preventDefault();
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
};

const CheckList = ({ items }) => (
  <ul className="fl2-check-list">
    {items.map((x) => (
      <li className="fl2-check-item" key={x}>
        <span className="fl2-check-badge"><FaCheck /></span>
        <span>{x}</span>
      </li>
    ))}
  </ul>
);

const FundingLabArea = () => {
  const [applyModalOpen, setApplyModalOpen] = useState(false);
  const [showMeetings, setShowMeetings] = useState(false);

  useEffect(() => {
    if (!showMeetings) return;

    // Load the HubSpot Meetings script if not already loaded
    const existingScript = document.querySelector('script[data-hs-meetings]');

    if (!existingScript) {
      const s = document.createElement("script");
      s.type = "text/javascript";
      s.src = "https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js";
      s.setAttribute('data-hs-meetings', 'true');
      s.onload = () => {
        if (window.HubSpotConversations) {
          window.HubSpotConversations.widget.refresh();
        }
      };
      document.body.appendChild(s);
    } else {
      setTimeout(() => {
        if (window.HubSpotConversations) {
          window.HubSpotConversations.widget.refresh();
        }
      }, 100);
    }
  }, [showMeetings]);

  // A single embed instance — the sidebar "Book a Call" button opens it and
  // scrolls the page there.
  const openMeetings = () => {
    setShowMeetings(true);
    requestAnimationFrame(() => {
      document.getElementById('book-a-call')?.scrollIntoView({ behavior: 'smooth' });
    });
  };

  const outcomesEnd = [
    "A funding strategy across grants, customer financing, angels, venture capital, debt, or a blended stack",
    "A prioritized pipeline of the right capital sources — in the right order",
    "The materials to execute: pipelines and templates ready to use, not just a plan",
    "A 90-day plan to go get it",
  ];

  const forWho = [
    "Founders who default to “raise VC money” without checking if it's the right fit",
    "Teams choosing between grants, customer financing, angel money, venture capital, or debt — and unsure of the order",
    "Companies that want a real pipeline and execution plan, not just a pitch deck",
    "Canadian founders who want a capital strategy built around non-dilutive options first",
  ];

  const problem = [
    "Founders default to pitching VCs before checking if it's the right capital source",
    "Grants and investors get pursued in the wrong order, wasting time and goodwill",
    "More equity gets given up than the stage requires",
    "There's no real pipeline — just a deck and a hope",
  ];

  const capitalStack = [
    { name: "Founder capital", tag: "validation", width: 100 },
    { name: "Grants & tax credits", tag: "R&D", width: 84 },
    { name: "Customer financing", tag: "product", width: 68 },
    { name: "Angel / SAFE", tag: "market entry", width: 52 },
    { name: "Revenue-based financing", tag: "growth", width: 36 },
  ];

  const paths = [
    {
      title: "Grant First",
      body: "For teams whose technical work and impact make them a strong non-dilutive candidate. Start with a ranked grant, tax-credit and competition pipeline before considering equity — then layer other sources on top.",
    },
    {
      title: "Revenue First",
      body: "For teams who can likely fund their next milestone through customers — pilots, pre-sales, deposits and design partnerships — before taking outside capital.",
    },
    {
      title: "Equity First",
      body: "For teams whose scale ambitions and market point toward equity, on their own terms. We confirm venture suitability, right-size the round, and build the deck, model and investor pipeline.",
    },
    {
      title: "Debt First",
      body: "For teams with predictable revenue who may be able to finance growth without diluting at all — loans, lines of credit and revenue-based financing, with the case lenders need to say yes.",
    },
    {
      title: "Blended Stack",
      body: "For teams where no single source fits. We sequence grants for R&D, customer cash for validation, equity for scale and debt for working capital into one deliberate capital stack.",
      blend: true,
    },
  ];

  const whyChoose = [
    "We start with non-dilutive and customer capital, not just equity",
    "We sequence grants, revenue, angels, VC and debt strategically",
    "We build the pipeline and materials — not just a strategy memo",
    "We're Canada-focused and know the non-dilutive landscape here",
    "We give you a 90-day plan to execute, not just advice",
  ];

  const programFormat = [
    { label: "Next Cohort", value: "Sept 18 – Nov 28" },
    { label: "Starts", value: "Sept 18, 8:00 AM PT" },
    { label: "Format", value: "Cohort-based" },
    { label: "Duration", value: "10 weeks" },
    { label: "Price", value: "CAD $950", sub: "CAD $1,650" },
    { label: "Approach", value: "Non-dilutive first" },
    { label: "Focus", value: "Canada-focused" },
  ];

  const mentors = [
    {
      name: "Mike Qureshi",
      role: "CEO, Evolvelab",
      linkedin: "https://www.linkedin.com/in/msqureshi/",
      photo: "assets/img/funding-lab/mike-qureshi.jpg",
    },
    {
      name: "Pankaj Bagga",
      role: "CEO, LaunchBC (VCC) Inc",
      linkedin: "https://www.linkedin.com/in/pankaj-bagga/",
      photo: "assets/img/funding-lab/pankaj-bagga.jpeg",
    },
    {
      name: "Beata Jirava",
      role: "CEO, Novel Tech",
      linkedin: "https://www.linkedin.com/in/beata-jir/",
      photo: "assets/img/funding-lab/beata-jirava.jpeg",
    },
  ];

  const partners = [
    { name: "Evolvelab", url: "https://evolvelab.co/", logo: "assets/img/funding-lab/evolvelab-logo.png" },
    { name: "Granthub", url: "https://granthub.ca/", logo: "assets/img/funding-lab/granthub-logo.png" },
    { name: "LaunchBC", url: "https://launchbc.ca/", logo: "assets/img/funding-lab/launchbc-logo.png" },
  ];

  const notIncluded = [
    "Grant writing or submissions",
    "Legal or accounting services",
    "Guaranteed funding outcomes",
  ];

  const afterProgram = [
    "A clear, sequenced capital strategy",
    "A pipeline of the right sources to pursue first",
    "Ready-to-use pitch and application materials",
    "A 90-day plan to execute",
    "Confidence you're not leaving easy capital on the table",
  ];

  const meetingsWidget = showMeetings && (
    <div className="mt-3">
      <div
        className="meetings-iframe-container"
        data-src="https://meetings.hubspot.com/mike-qureshi?embed=true"
      ></div>
    </div>
  );

  return (
    <>
      {/* ===================== Funding Lab (v2) start ===================== */}
      <div className="service-area pd-bottom-90 funding-lab-v2">
        <div className="container">
          <div className="row">
            {/* LEFT SIDEBAR — below the main content on mobile/tablet, left column on desktop */}
            <div className="col-lg-4 col-12 order-2 order-lg-1">
              <div className="td-sidebar service-sidebar">
                <div className="fl2-sidebar-card">
                  <h5 className="widget-title" style={{ color: '#50af9b' }}>
                    <FaArrowRight />Funding Program
                  </h5>
                  <ul className="catagory-items">
                    <li><Link to="/funding/jumpstart">Evolvelab Fund Jumpstart</Link></li>
                    <li><Link to="/funding/boost">Evolvelab Fund Boost</Link></li>
                    <li><Link to="/funding/advisory">Evolvelab Fund Advisory</Link></li>
                    <li><Link to="/fundinglab">Evolvelab Funding Lab</Link></li>
                  </ul>

                  <div style={{ height: '16px' }}></div>
                  <h5 className="widget-title" style={{ color: '#50af9b' }}>
                    <FaArrowRight />Dev Accelerator
                  </h5>
                  <ul className="catagory-items">
                    <li><Link to="/accelerator/dev-sprint">Evolvelab Dev Sprint</Link></li>
                    <li><Link to="/accelerator/dev-build">Evolvelab Dev Build</Link></li>
                    <li><Link to="/accelerator/dev-scale">Evolvelab Dev Scale</Link></li>
                  </ul>
                </div>

                <div className="fl2-sidebar-card text-center">
                  <button
                    type="button"
                    className="btn btn-base-color border-radius-5 d-flex align-items-center justify-content-center w-100"
                    onClick={openMeetings}
                  >
                    📅 Book a Free Funding Readiness Call
                  </button>
                </div>

                <div className="fl2-sidebar-card">
                  <h5 className="widget-title" style={{ color: '#50af9b' }}>
                    <FaArrowRight /> Funding Lab Snapshot
                  </h5>
                  <ul className="fl2-snapshot-list">
                    {programFormat.map((row) => (
                      <li className="fl2-snapshot-item" key={row.label}>
                        <a href="#program-format" onClick={scrollToId('program-format')} style={{ color: 'inherit', textDecoration: 'none', display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                          <span>{row.label}</span>
                          <b>{row.value}</b>
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* RIGHT CONTENT */}
            <div className="col-lg-8 order-1 order-lg-2">
              {/* HERO */}
              <div className="fl2-hero">
                <div className="fl2-hero-content">
                  <span className="fl2-eyebrow">A 10-week capital-readiness program</span>
                  <h1 className="fl2-hero-title">
                    Don't just raise money. Engineer your capital stack.
                  </h1>
                  <p className="fl2-hero-lede">
                    Funding Lab turns your next growth milestone into an executable capital campaign. In 10 weeks, identify the right combination of non-dilutive grants, customer financing, equity and debt; build the applications, financial case, data room and qualified pipelines behind it; and begin pursuing capital with evidence, leverage and a clear closing plan.
                  </p>
                  <div className="fl2-meta-row">
                    <span className="fl2-meta-chip">Non-dilutive first</span>
                    <span className="fl2-meta-chip">Canada-focused</span>
                    <span className="fl2-meta-chip">10 weeks</span>
                    <span className="fl2-meta-chip">Cohort-based</span>
                  </div>
                  <div className="fl2-hero-cta">
                    <button
                      type="button"
                      className="fl2-btn fl2-btn-solid fl2-btn-lg"
                      style={{ border: 'none', cursor: 'pointer' }}
                      onClick={() => setApplyModalOpen(true)}
                    >
                      🚀 Apply to the Next Cohort
                    </button>
                  </div>
                </div>
              </div>
              <div id="book-a-call">{meetingsWidget}</div>

              {/* KEY OUTCOME LIST */}
              <div className="fl2-section">
                <h2 className="fl2-section-title">By the end of Funding Lab, you will have:</h2>
                <CheckList items={outcomesEnd} />
              </div>

              {/* WHO IT'S FOR */}
              <div className="fl2-section">
                <h2 className="fl2-section-title">Who Funding Lab Is For</h2>
                <p className="fl2-section-kicker">Funding Lab is built for:</p>
                <CheckList items={forWho} />
                <p className="mt-3 mb-0">If you want a real capital stack — not just a pitch deck — this is for you.</p>
              </div>

              {/* PROBLEM */}
              <div className="fl2-section">
                <h2 className="fl2-section-title">The Problem We Solve</h2>
                <p className="fl2-section-kicker">Most founders don't raise the right capital, in the right order, because:</p>
                <CheckList items={problem} />
                <p className="mt-3 mb-0"><b>Funding Lab replaces guesswork with a sequenced capital stack.</b></p>
              </div>

              {/* CAPITAL STACK */}
              <div className="fl2-section">
                <h2 className="fl2-section-title">Your Capital Stack</h2>
                <p className="fl2-section-kicker">A startup doesn't need one funding source. It needs a stack — in the right order.</p>
                <div className="fl2-stack">
                  {capitalStack.map((row) => (
                    <div className="fl2-stack-row" key={row.name}>
                      <span className="fl2-stack-name">{row.name}</span>
                      <span className="fl2-stack-track">
                        <span className="fl2-stack-fill" style={{ width: `${row.width}%` }}></span>
                      </span>
                      <span className="fl2-stack-tag">{row.tag}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* PATHS */}
              <div className="fl2-section" id="how-it-works">
                <h2 className="fl2-section-title">Which Path Fits You</h2>
                <p className="fl2-section-kicker">Every founder's starting point is different. Funding Lab helps you find yours:</p>
                <div className="fl2-path-grid">
                  {paths.map((p) => (
                    <div className={`fl2-path-card ${p.blend ? 'fl2-path-blend' : ''}`} key={p.title}>
                      <h3 className="fl2-path-title">{p.title}</h3>
                      <p className="fl2-path-body">{p.body}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* WHY CHOOSE */}
              <div className="fl2-section">
                <h2 className="fl2-section-title">Why Founders Choose Evolvelab</h2>
                <CheckList items={whyChoose} />
                <p className="mt-3 mb-0">We act as your fractional funding CXO, not a one-off advisor.</p>
              </div>

              {/* MENTORS */}
              <div className="fl2-section" id="mentors">
                <h2 className="fl2-section-title">Mentors</h2>
                <p className="fl2-section-kicker">Learn directly from operators and investors who've done this before.</p>
                <div className="fl2-mentor-grid">
                  {mentors.map((m) => (
                    <a
                      key={m.name}
                      href={m.linkedin}
                      target="_blank"
                      rel="noreferrer"
                      className="fl2-mentor-card"
                    >
                      <img src={m.photo} alt={m.name} className="fl2-mentor-photo" />
                      <span className="fl2-mentor-name">{m.name}</span>
                      <span className="fl2-mentor-role">{m.role}</span>
                    </a>
                  ))}
                </div>
              </div>

              {/* PARTNERS */}
              <div className="fl2-section" id="partners">
                <h2 className="fl2-section-title">Partners</h2>
                <p className="fl2-section-kicker">Delivered in partnership with organizations active in the Canadian funding ecosystem.</p>
                <div className="fl2-partner-grid">
                  {partners.map((p) => (
                    <a
                      key={p.name}
                      href={p.url}
                      target="_blank"
                      rel="noreferrer"
                      className="fl2-partner-card"
                      aria-label={p.name}
                    >
                      <img src={p.logo} alt={p.name} className="fl2-partner-logo" />
                    </a>
                  ))}
                </div>
              </div>

              {/* PROGRAM FORMAT */}
              <div className="fl2-section" id="program-format">
                <h2 className="fl2-section-title">Program Format</h2>
                <div className="fl2-stat-grid">
                  {programFormat.map((row) => (
                    <div className="fl2-stat-card" key={row.label}>
                      <span className="fl2-stat-label">{row.label}</span>
                      <span className="fl2-stat-value">
                        {row.value}
                        {row.sub && <span className="fl2-stat-strike">{row.sub}</span>}
                      </span>
                    </div>
                  ))}
                </div>
                <p className="mt-3 mb-0"><b>Spots are limited — apply now to secure your seat in this cohort.</b></p>
              </div>

              {/* NOT INCLUDED */}
              <div className="fl2-section">
                <h2 className="fl2-section-title">What This Is Not</h2>
                <p className="fl2-section-kicker">To stay focused, Funding Lab does not include:</p>
                <CheckList items={notIncluded} />
                <p className="mt-3 mb-0">(These can be added later — once the strategy is clear.)</p>
              </div>

              {/* OUTCOME */}
              <div className="fl2-section">
                <h2 className="fl2-section-title">The Outcome</h2>
                <p className="fl2-section-kicker">After Funding Lab, you will have:</p>
                <CheckList items={afterProgram} />
                <p className="mt-3 mb-0"><b>You stop guessing. You start executing.</b></p>
              </div>

              {/* FAQ */}
              <div className="fl2-section">
                <h2 className="fl2-section-title">Get in touch — have any questions?</h2>
                <p className="fl2-section-kicker">Quick answers before you apply to Funding Lab.</p>

                <div className="accordion accordion-inner accordion-icon-left fl2-faq" id="fundingLabAccordion">
                  <div className="accordion-item">
                    <h3 className="accordion-header" id="flHeadingOne">
                      <button
                        className="accordion-button"
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target="#flCollapseOne"
                        aria-expanded="true"
                        aria-controls="flCollapseOne"
                      >
                        What is Funding Lab?
                      </button>
                    </h3>
                    <div
                      id="flCollapseOne"
                      className="accordion-collapse collapse show"
                      aria-labelledby="flHeadingOne"
                      data-bs-parent="#fundingLabAccordion"
                    >
                      <div className="accordion-body">
                        A 10-week, cohort-based program that helps you design and execute the right capital
                        strategy — grants, customer financing, angels, venture capital, debt, or a blended
                        stack — with the pipeline, materials and 90-day plan to go get it.
                      </div>
                    </div>
                  </div>

                  <div className="accordion-item">
                    <h3 className="accordion-header" id="flHeadingTwo">
                      <button
                        className="accordion-button collapsed"
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target="#flCollapseTwo"
                        aria-expanded="false"
                        aria-controls="flCollapseTwo"
                      >
                        How long does it take?
                      </button>
                    </h3>
                    <div
                      id="flCollapseTwo"
                      className="accordion-collapse collapse"
                      aria-labelledby="flHeadingTwo"
                      data-bs-parent="#fundingLabAccordion"
                    >
                      <div className="accordion-body">
                        Funding Lab runs for 10 weeks. You'll leave with a sequenced strategy, a pipeline, and
                        a 90-day plan to execute it.
                      </div>
                    </div>
                  </div>

                  <div className="accordion-item">
                    <h3 className="accordion-header" id="flHeadingThree">
                      <button
                        className="accordion-button collapsed"
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target="#flCollapseThree"
                        aria-expanded="false"
                        aria-controls="flCollapseThree"
                      >
                        How much does it cost?
                      </button>
                    </h3>
                    <div
                      id="flCollapseThree"
                      className="accordion-collapse collapse"
                      aria-labelledby="flHeadingThree"
                      data-bs-parent="#fundingLabAccordion"
                    >
                      <div className="accordion-body">
                        CAD $950 for the next cohort (regular price CAD $1,650). Apply and we'll walk you
                        through payment details.
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* FINAL CONVERSION */}
              <div className="fl2-cta-band">
                <h2 className="fl2-cta-title">Ready to Build Your Capital Stack?</h2>
                <p className="fl2-cta-lede">
                  If you want a sequenced funding strategy — not just a pitch deck — Funding Lab is your first step.
                </p>
                <div className="fl2-cta-buttons">
                  <button
                    type="button"
                    className="fl2-btn fl2-btn-solid fl2-btn-lg"
                    style={{ border: 'none', cursor: 'pointer' }}
                    onClick={() => setApplyModalOpen(true)}
                  >
                    🚀 Apply to the Next Cohort
                  </button>
                </div>
              </div>
            </div>

          </div>
        </div>

        <FundingLabApplyModal open={applyModalOpen} onClose={() => setApplyModalOpen(false)} />
      </div>
      {/* ===================== Funding Lab (v2) End ===================== */}
    </>
  );
};

export default FundingLabArea;
